/** @flow */

import React from "react";
import { ComboBox } from "@components";
import {
  transformSecondsInHours,
  transformSecondsInMinutes,
  transformHoursInSeconds,
  transformMinutesInSeconds,
  addLeadingZero,
} from "@transformers/date";
import classNames from "classnames";
import type { OptionType } from "@components";

type Props = $Exact<{
  className?: string,
  seconds: number,
  onChange: (seconds: number) => void,
}>;

type ComponentState = $Exact<{
  hours: number,
  minutes: number,
}>;

export class HoursMinutesDropdown extends React.Component<Props, ComponentState> {
  static defaultProps = {
    seconds: 0,
  };

  constructor(props: Props) {
    super(props);

    this.state = this.getStateValues(props.seconds);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.seconds !== prevProps.seconds) {
      this.setState(this.getStateValues(this.props.seconds));
    }
  }

  getStateValues = (seconds: number) => {
    const hours = transformSecondsInHours(seconds, false, true);
    const minutes = parseInt(transformSecondsInMinutes(seconds - transformHoursInSeconds(hours)));
    return {
      hours,
      minutes,
    };
  };

  get hoursOptions(): Array<OptionType> {
    return [...new Array(24)].map((_, idx) => ({
      value: idx,
      label: addLeadingZero(idx),
    }));
  }

  get minutesOptions(): Array<OptionType> {
    return [...new Array(60)].map((_, idx) => ({
      value: idx,
      label: addLeadingZero(idx),
    }));
  }

  handleHoursChange = (val: OptionType) => {
    this.setState({
      hours: val,
    });

    const totalSeconds =
      parseInt(transformHoursInSeconds(val)) +
      parseInt(transformMinutesInSeconds(this.state.minutes));
    this.props.onChange(totalSeconds);
  };

  handleMinutesChange = (val: OptionType) => {
    this.setState({
      minutes: val,
    });

    const totalSeconds =
      parseInt(transformHoursInSeconds(this.state.hours)) +
      parseInt(transformMinutesInSeconds(val));
    this.props.onChange(totalSeconds);
  };

  render() {
    return (
      <div className="d-flex flex-1">
        <ComboBox
          className="flex-1"
          key={`${this.props.inputId}-hours-input`}
          inputId={`${this.props.inputId}-hours-input`}
          id={`${this.props.inputId}-hours`}
          label="HH"
          options={this.hoursOptions}
          value={{
            label: addLeadingZero(this.state.hours),
            value: this.state.hours,
          }}
          onChange={this.handleHoursChange}
          withoutEmptyOption
        />
        <ComboBox
          className="flex-1"
          key={`${this.props.inputId}-minutes-input`}
          inputId={`${this.props.inputId}-minutes-input`}
          id={`${this.props.inputId}-minutes`}
          label="MM"
          options={this.minutesOptions}
          value={{
            label: addLeadingZero(this.state.minutes),
            value: this.state.minutes,
          }}
          onChange={this.handleMinutesChange}
          withoutEmptyOption
        />
      </div>
    );
  }
}

export default HoursMinutesDropdown;
